import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import './OcrUpload.css';
import api from '../services/api';

// eslint-disable-next-line no-unused-vars
const UPLOAD_DELAY_MS = parseInt(process.env.REACT_APP_UPLOAD_DELAY_MS ?? '200', 10) || 200;
const FINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CLEARED']);

const fileKey = (file) => file?.webkitRelativePath || file?.path || file?.name || '';

const ensureRelativePath = (file, fullPath) => {
  if (!file.webkitRelativePath) {
    Object.defineProperty(file, 'webkitRelativePath', {
      configurable: true,
      enumerable: true,
      value: fullPath.replace(/^\//, ''),
    });
  }
  return file;
};

// Must match SUPPORTED_UPLOAD_EXTENSIONS in ocr/views.py — the backend
// rejects anything else, so filter client-side and tell the user instead of
// uploading files that will immediately fail.
const SUPPORTED_UPLOAD_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff'];

function isSupportedUpload(file) {
  const name = (file.name || '').toLowerCase();
  return SUPPORTED_UPLOAD_EXTENSIONS.some(ext => name.endsWith(ext));
}

async function collectFilesFromEntries(entries, out) {
  const traverse = async (entry) => {
    if (entry.isFile) {
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      out.push(ensureRelativePath(file, entry.fullPath || file.name));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      const readEntries = () => new Promise((resolve, reject) => reader.readEntries(resolve, reject));
      let batch;
      do {
        batch = await readEntries();
        for (const child of batch) {
          await traverse(child);
        }
      } while (batch.length);
    }
  };

  for (const entry of entries) {
    await traverse(entry);
  }
}

function OcrUpload({ user }) {
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [pendingDocs, setPendingDocs] = useState([]);
  const [readyDocs, setReadyDocs] = useState([]);
  const [error, setError] = useState(null);
  // Obfuscated OCR: slice pages into bands and shuffle them before Document AI.
  const [obfuscate, setObfuscate] = useState(false);
  // Download state
  // eslint-disable-next-line no-unused-vars
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState('');
  // Async bulk download state
  const [bulkDownloadTask, setBulkDownloadTask] = useState(null);
  const pollers = useRef({});
  const fileInputRef = useRef(null);

  // ...existing code...

  const ensureCsrf = useCallback(async () => {
    try {
      await api.get('ocr/csrf/', { validateStatus: () => true });
    } catch (err) {
      console.warn('CSRF bootstrap failed', err);
    }
  }, []);

  const buildEndpoint = (path) => {
    if (!path) return null;
    const trimmed = path.startsWith('/') ? path.slice(1) : path;
    return trimmed.startsWith('ocr/') ? trimmed : `ocr/${trimmed}`;
  };

  const convertDocToItem = useCallback((doc) => {
    const status = (doc.status || '').toLowerCase();
    const name = doc.meta?.relative_path || doc.file_name;
    return {
      docId: doc.id,
      file: null,
      name,
      idKey: name,
      sourceName: doc.file_name,
      progress: doc.status === 'COMPLETED' ? 100 : 0,
      status,
      result: doc,
      error: doc.meta?.error || null,
    };
  }, []);

  const mergeDocument = useCallback((doc) => {
    const status = (doc.status || '').toLowerCase();
    const docKey = doc.meta?.relative_path || doc.file_name;
    const docName = doc.file_name;
    setItems(prev => {
      if (status === 'cleared') {
        return prev.filter(it => it.docId !== doc.id && it.idKey !== docKey);
      }

      const mapped = prev.map(it => {
        const candidates = [it.idKey, it.sourceName, it.file?.name].filter(Boolean);
        if (it.docId === doc.id || (!it.docId && candidates.includes(docKey)) || (!it.docId && candidates.includes(docName))) {
          return {
            ...it,
            docId: doc.id,
            status,
            idKey: docKey,
            name: docKey,
            sourceName: docName,
            progress: doc.status === 'COMPLETED' ? 100 : it.progress || 100,
            result: doc,
            error: doc.meta?.error || null,
          };
        }
        return it;
      });

      const exists = mapped.some(it => it.docId === doc.id);
      if (!exists) {
        mapped.unshift(convertDocToItem(doc));
      }
      return mapped;
    });
  }, [convertDocToItem]);

  const stopPolling = (docId) => {
    if (pollers.current[docId]) {
      clearInterval(pollers.current[docId]);
      delete pollers.current[docId];
    }
  };

  const fetchDocument = useCallback(async (docId) => {
    if (!docId) return;
    try {
      const res = await api.get(`ocr/documents/${docId}/`);
      const doc = res.data;
      mergeDocument(doc);
      if (FINAL_STATUSES.has(doc.status)) {
        stopPolling(docId);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        stopPolling(docId);
        setItems(prev => prev.filter(it => it.docId !== docId));
      } else {
        console.error('Poll error', err);
      }
    }
  }, [mergeDocument]);

  const startPolling = useCallback((docId) => {
    if (!docId || pollers.current[docId]) return;
    fetchDocument(docId);
    pollers.current[docId] = setInterval(() => fetchDocument(docId), 4000);
  }, [fetchDocument]);

  const refreshCategorized = useCallback(async () => {
    try {
      const [pendingRes, readyRes] = await Promise.all([
        api.get('ocr/documents/pending/'),
        api.get('ocr/documents/ready/'),
      ]);
      const pending = pendingRes.data?.results || [];
      const ready = readyRes.data?.results || [];
      setPendingDocs(pending);
      setReadyDocs(ready);
      pending.forEach(doc => {
        if (doc.id && !FINAL_STATUSES.has(doc.status)) {
          startPolling(doc.id);
        }
      });
    } catch (e) {
      console.warn('Categorized refresh failed', e);
    }
  }, [startPolling]);

  const ACTIVE_KEY = 'ocr_active_docs_v1';

  useEffect(() => {
    let canceled = false;
    const loadExisting = async () => {
      try {
        const res = await api.get('ocr/documents/');
        if (canceled) return;
        const docs = (res.data?.results ?? []).filter(doc => (doc.status || '').toLowerCase() !== 'cleared');
        setItems(docs.map(convertDocToItem));
        docs.forEach(doc => {
          if (!FINAL_STATUSES.has(doc.status)) {
            startPolling(doc.id);
          }
        });
      } catch (err) {
        if (!canceled) {
          console.error(err);
          setError('Unable to load previous uploads.');
        }
      } finally {
        if (!canceled) refreshCategorized();
      }
    };

    ensureCsrf();
    loadExisting();

    try {
      const saved = JSON.parse(localStorage.getItem(ACTIVE_KEY) || '[]');
      if (Array.isArray(saved)) {
        saved.forEach(id => startPolling(id));
      }
    } catch { /* ignore */ }

    return () => {
      canceled = true;
      Object.values(pollers.current).forEach(clearInterval);
    };
  }, [ensureCsrf, startPolling, convertDocToItem, refreshCategorized]);

  useEffect(() => {
    const activeIds = items
      .filter(it => {
        const st = (it.result?.status || it.status || '').toUpperCase();
        return it.docId && !FINAL_STATUSES.has(st);
      })
      .map(it => it.docId);
    try { localStorage.setItem(ACTIVE_KEY, JSON.stringify(activeIds)); } catch { /* ignore */ }
  }, [items]);

  useEffect(() => {
    const hasActive = () => items.some(it => {
      const st = (it.result?.status || it.status || '').toUpperCase();
      return it.docId && !FINAL_STATUSES.has(st);
    });
    const handler = (e) => {
      if (hasActive()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [items]);

  useEffect(() => {
    const visHandler = () => {
      if (document.visibilityState === 'visible') {
        refreshCategorized();
      }
    };
    document.addEventListener('visibilitychange', visHandler);
    return () => document.removeEventListener('visibilitychange', visHandler);
  }, [refreshCategorized]);

  useEffect(() => {
    refreshCategorized();
    const interval = setInterval(refreshCategorized, 5000);
    return () => clearInterval(interval);
  }, [refreshCategorized]);

  const onDrop = useCallback(accepted => {
    const supported = accepted.filter(isSupportedUpload);
    const skipped = accepted.filter(f => !isSupportedUpload(f));
    if (skipped.length) {
      const names = skipped.slice(0, 5).map(f => f.name).join(', ');
      const more = skipped.length > 5 ? ` and ${skipped.length - 5} more` : '';
      setError(`Skipped ${skipped.length} unsupported file(s): ${names}${more}. Supported: PDF, JPG, PNG, TIFF.`);
    } else {
      setError(null);
    }
    const newItems = supported.map(f => {
      const key = fileKey(f);
      return {
        file: f,
        name: key,
        idKey: key,
        sourceName: f.name,
        progress: 0,
        status: 'queued',
        result: null,
        error: null,
        docId: null,
      };
    });
    setFiles(prev => [...prev, ...supported]);
    setItems(prev => [...newItems, ...prev]);
  }, []);

  const getFilesFromEvent = useCallback(async (event) => {
    const inputFiles = event.target?.files;
    if (inputFiles && inputFiles.length) {
      return Array.from(inputFiles);
    }

    const items = event.dataTransfer?.items;
    if (!items) return [];

    const entries = [];
    for (let i = 0; i < items.length; i += 1) {
      const entry = items[i].webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }

    const files = [];
    await collectFilesFromEntries(entries, files);
    return files;
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    getFilesFromEvent,
    multiple: true,
  });

  const removeFile = (targetName) => {
    setFiles(prev => prev.filter(x => fileKey(x) !== targetName));
    setItems(prev => prev.filter(x => !(x.idKey === targetName && !x.docId)));
  };

  const formatStatus = (s) => {
    if (!s) return '';
    const key = s.toLowerCase();
    switch (key) {
      case 'queued':
        return 'Queued';
      case 'uploading':
        return 'Uploading';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cleared':
        return 'Cleared';
      case 'error':
        return 'Error';
      default:
        return key.charAt(0).toUpperCase() + key.slice(1);
    }
  };

  const statusClass = (s) => {
    const key = (s || '').toLowerCase();
    switch (key) {
      case 'queued':
        return 'queued';
      case 'uploading':
        return 'uploading';
      case 'processing':
        return 'processing';
      case 'completed':
        return 'done';
      case 'failed':
      case 'error':
        return 'error';
      case 'cleared':
        return 'cleared';
      default:
        return '';
    }
  };

  const handleUpload = async () => {
    if (!files.length) {
      setError('Select files first');
      return;
    }
    setError(null);
    await ensureCsrf();

    api.get('ocr/debug/session/').then(dbg => {
      console.log('Pre-upload session debug', dbg.data);
    }).catch(() => {});

    const CONCURRENCY = 5;
    let active = 0;
    const queue = [...files];

    const launchNext = () => {
      if (active >= CONCURRENCY) return;
      const f = queue.shift();
      if (!f) return;
      active += 1;
      const localKey = fileKey(f);
      setItems(prev => prev.map(it => (it.idKey === localKey && !it.docId)
        ? { ...it, status: 'uploading', progress: 0 }
        : it));

      const formData = new FormData();
      formData.append('file', f);
      const relPath = f.webkitRelativePath || f.path || f.name;
      formData.append('relative_path', relPath);
      formData.append('obfuscate', obfuscate ? 'true' : 'false');

      api.post('ocr/upload/', formData, {
        onUploadProgress: evt => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setItems(prev => prev.map(it => (it.idKey === localKey && !it.docId)
              ? { ...it, progress: pct, status: 'uploading' }
              : it));
          }
        }
      }).then(response => {
        const results = response.data.results || [];
        const match = results.find(r => (r.meta?.relative_path || r.file_name) === localKey)
          || results.find(r => r.file_name === f.name)
          || results[0]
          || null;
        if (match) {
          mergeDocument(match);
          startPolling(match.id);
        }
      }).catch(e => {
        const msg = e?.response?.data?.error || e.message || 'Upload failed';
        setItems(prev => prev.map(it => (it.idKey === localKey && !it.docId)
          ? { ...it, status: 'error', error: msg }
          : it));
      }).finally(() => {
        active -= 1;
        setFiles(prev => prev.filter(x => x !== f));
        launchNext();
      });

      launchNext();
    };

    for (let i = 0; i < CONCURRENCY; i += 1) {
      launchNext();
    }
  };

  const downloadFile = async (path, defaultName) => {
    const endpoint = buildEndpoint(path);
    if (!endpoint) return;
    try {
      const res = await api.get(endpoint, {
        responseType: 'blob',
        params: { clear: false },
      });
      // ✅ Force binary download to prevent browser from opening PDF
      // Using octet-stream ensures browser downloads instead of displaying inline
      const blob = new Blob([res.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (defaultName || 'file').split(/[\\/]/).pop();
      a.download = safeName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Download failed');
    }
  };

  const handleAsyncDownloadAll = async (clearAfter = false) => {
    if (bulkDownloadTask) return;
    
    const docIds = readyDocs.map(doc => doc.id);
    
    if (docIds.length === 0) {
      setDownloadMessage('No documents ready to download');
      setTimeout(() => setDownloadMessage(''), 3000);
      return;
    }
    
    setDownloadMessage(`Starting download preparation for ${docIds.length} documents…`);
    
    try {
      const prepareRes = await api.post('ocr/bulk-download/prepare/', {
        doc_ids: docIds,
        clear: clearAfter
      });
      
      const taskId = prepareRes.data.task_id;
      if (!taskId) {
        throw new Error('No task ID returned from server');
      }
      
      setBulkDownloadTask({ taskId, clearAfter });
      setDownloadMessage('Creating ZIP archive in background…');
      
      pollBulkDownloadStatus(taskId, clearAfter);
    } catch (err) {
      console.error('Failed to start async download:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to start download';
      setError(errorMsg);
      setDownloadMessage('');
      setBulkDownloadTask(null);
    }
  };



const pollBulkDownloadStatus = (taskId, clearAfter) => {
  let pollCount = 0;
  const maxPolls = 300;
  
  const pollInterval = setInterval(async () => {
    pollCount++;
    
    if (pollCount > maxPolls) {
      clearInterval(pollInterval);
      setError('Download preparation timed out. Please try again with fewer files.');
      setDownloadMessage('');
      setBulkDownloadTask(null);
      return;
    }
    
    try {
      const statusRes = await api.get(`ocr/bulk-download/status/${taskId}/`);
      const data = statusRes.data;
      
      console.log('Poll response:', data);
      
      if (data.state === 'SUCCESS') {
        clearInterval(pollInterval);
        
        // FIX: Check for both zip_url and download_url
        const zipUrl = data.zip_url || data.download_url;
        
        if (!zipUrl) {
          console.error('Task succeeded but no zip_url/download_url in result:', data);
          setError('Download completed but no file URL provided');
          setDownloadMessage('');
          setBulkDownloadTask(null);
          return;
        }
        
        const fileCount = data.file_count || 'unknown';
        const zipSizeMB = data.zip_size ? (data.zip_size / (1024 * 1024)).toFixed(2) : 'unknown';
        setDownloadMessage(`ZIP ready! ${fileCount} files (${zipSizeMB} MB). Downloading…`);
        
        const downloadUrl = zipUrl.startsWith('http') 
          ? zipUrl 
          : `${window.location.origin}${zipUrl}`;
        
        console.log('Triggering download:', downloadUrl);
        
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = data.zip_name || 'ocr_outputs.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        
        setBulkDownloadTask(null);
        
        if (clearAfter) {
          setDownloadMessage('Download started. Files cleared. Refreshing page…');
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          setDownloadMessage('Download started successfully!');
          setTimeout(() => {
            setDownloadMessage('');
            refreshCategorized();
          }, 3000);
        }
        
      } else if (data.state === 'FAILURE') {
        clearInterval(pollInterval);
        const errorMsg = data.error || (data.result && data.result.error) || 'Download preparation failed';
        console.error('Async download task failed:', errorMsg);
        setError(errorMsg);
        setDownloadMessage('');
        setBulkDownloadTask(null);
        
      } else if (data.state === 'PENDING' || data.state === 'STARTED') {
        const progressMsg = data.progress 
          ? `Creating ZIP archive… ${data.progress}` 
          : 'Creating ZIP archive…';
        setDownloadMessage(progressMsg);
        
      } else {
        setDownloadMessage(`Processing… (${data.state})`);
      }
      
    } catch (err) {
      clearInterval(pollInterval);
      console.error('Failed to poll download status:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to check download status';
      setError(errorMsg);
      setDownloadMessage('');
      setBulkDownloadTask(null);
    }
  }, 1000);
};


  const clearOutputsNow = async () => {
    if (!window.confirm('Delete all OCR output files on the server now?')) return;
    try {
      await api.get('ocr/csrf/', { validateStatus: () => true }).catch(() => {});
      const response = await api.post('ocr/clear/');
      
      // Handle async cleanup (HTTP 202)
      if (response.status === 202) {
        alert(`Cleanup started in background for ${response.data.document_count} documents. Refreshing in 2 seconds...`);
        // Refresh after 2 seconds to show updated state
        setTimeout(() => {
          fetchDocument();
        }, 2000);
      } else {
        // Old sync response (HTTP 200) - clear immediately
        Object.values(pollers.current).forEach(clearInterval);
        pollers.current = {};
        setItems(prev => prev.filter(it => !it.docId));
      }
    } catch (e) {
      console.error(e);
      alert('Clear outputs failed');
    }
  };

  const handleFileSelection = useCallback((event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length) {
      onDrop(selected);
    }
    if (event.target) {
      event.target.value = '';
    }
  }, [onDrop]);

  const handleSelectFilesClick = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  const totalItems = items.length;
  const processedItems = items.filter(it => {
    const st = (it.result?.status || it.status || '').toUpperCase();
    return FINAL_STATUSES.has(st);
  }).length;
  const progressPct = totalItems ? Math.round((processedItems / totalItems) * 100) : 0;
  const allDone = totalItems > 0 && processedItems === totalItems;

  const downloadAllDisabled = downloadingAll || !allDone || processedItems === 0;

  return (
    <div className="ocr-upload-container">
      <h2>Upload for OCR</h2>

      {downloadMessage && (
        <div className="download-notice" role="status" aria-live="polite">
          {downloadMessage}
        </div>
      )}

      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps({ webkitdirectory: 'true', directory: 'true' })} multiple />
        <p>Drag & drop or click to select folders</p>
      </div>

      <div className="upload-single-row">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="application/pdf,.pdf,image/jpeg,image/png,image/tiff,.jpg,.jpeg,.png,.tif,.tiff"
          multiple
          onChange={handleFileSelection}
        />
        <button
          type="button"
          className="action-btn primary upload-individual-btn"
          onClick={handleSelectFilesClick}
        >
          Upload Individual PDF / Image
        </button>
      </div>

      <div className="controls-row">
        <label className="obfuscate-toggle" title="Each page is cut into horizontal bands at line-free positions and the bands are shuffled onto carrier pages before OCR, so the OCR service never receives a readable page. The searchable PDF you get back is reassembled in the original layout.">
          <input
            type="checkbox"
            checked={obfuscate}
            onChange={e => setObfuscate(e.target.checked)}
          />
          {' '}Obfuscate (slice &amp; shuffle pages before OCR)
        </label>
      </div>

      <div className="controls-row">
        <button
          type="button"
          onClick={() => handleAsyncDownloadAll(true)}
          className="action-btn warning"
          disabled={downloadAllDisabled || bulkDownloadTask}
          title="Download all and clear files after"
        >
          {bulkDownloadTask ? (
            <>
              <span className="spinner" aria-hidden="true" /> Preparing…
            </>
          ) : 'Download & Clear'}
        </button>

        <button
          type="button"
          onClick={clearOutputsNow}
          className="action-btn danger"
          disabled={bulkDownloadTask}
        >
          Clear Server Files
        </button>
      </div>

      {totalItems > 0 && (
        <div className="upload-stats" aria-label="Overall processing progress">
          <div className="stats-line">
            <span className="stat-label">Processed:</span>
            <span className="stat-values">
              {processedItems} of {totalItems} ({progressPct}%)
            </span>
            {!allDone && <span className="stat-hint">Processing in progress…</span>}
            {allDone && <span className="stat-hint done">All done</span>}
          </div>
          <div className="progress-track" role="progressbar"
               aria-valuenow={progressPct}
               aria-valuemin={0}
               aria-valuemax={100}
               aria-label="Overall progress">
            <div className="progress-bar" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      {files.length > 0 && (
        <button onClick={handleUpload} className="upload-btn action-btn primary">Upload &amp; Process</button>
      )}

      {error && <p className="error-message">{error}</p>}

      {items.length > 0 && (
        <ul className="file-status-list">
          {items.map(it => (
            <li key={it.docId ? `doc-${it.docId}` : `local-${it.idKey}`}>
              <div className="file-info">
                <strong className="file-name">{it.name}</strong>
                <span className={`status-badge ${statusClass(it.status)}`}>
                  {formatStatus(it.status)}
                </span>
                {it.status === 'uploading' && <span className="upload-progress"> {it.progress}%</span>}
                {it.result?.meta?.obfuscated && (() => {
                  const o = it.result.meta.obfuscated;
                  const d = (o.documents && o.documents[0]) || {};
                  return (
                    <span className="status-badge status-obfuscated" title={`seed ${o.seed} · ${o.tokens_mapped}/${o.tokens_total} tokens mapped`}>
                      sliced · {d.bands ?? '?'} bands / {d.pages ?? o.source_pages} pages → {o.carrier_pages} carrier{o.carrier_pages === 1 ? '' : 's'} · dropped {o.tokens_dropped}
                    </span>
                  );
                })()}
                {it.error && <span className="item-error"> Error: {it.error}</span>}
              </div>

              <div className="file-actions">
                {it.result?.download_pdf_url && (
                  <button
                    type="button"
                    className="download-btn"
                    onClick={() => downloadFile(it.result.download_pdf_url, it.result.file_name?.replace(/\.pdf$/i, '') + '_searchable.pdf')}
                  >
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginRight: '6px', verticalAlign: 'text-bottom' }}
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Download
                  </button>
                )}
                {it.result?.download_json_url && (
                  <button
                    type="button"
                    className="download-btn secondary"
                    onClick={() => downloadFile(it.result.download_json_url, it.result.file_name?.replace(/\.pdf$/i, '') + '.json')}
                  >
                    JSON
                  </button>
                )}
                {it.docId && !FINAL_STATUSES.has((it.result?.status) || it.status?.toUpperCase()) && (
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => fetchDocument(it.docId)}
                  >
                    Refresh
                  </button>
                )}
                {!it.docId && (
                  <button onClick={() => removeFile(it.name)} className="remove-file-btn">
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default OcrUpload;